// Product data for LEION Engineering
export const PRODUCT_CATEGORIES = [
  {
    id: 'weighbridges',
    name: 'Weighbridges',
    description: 'Industrial truck scales for accurate vehicle weighing',
    image: '/assets/product-logo-pitless-weighbridge.png',
    imageFit: 'cover',
  },
  {
    id: 'indicators',
    name: 'Weight Indicators',
    description: 'Digital weight indicators and terminals',
    image: '/assets/hitech-combi-indicator.jpg',
    imageFit: 'contain',
  },
  {
    id: 'table-top-scales',
    name: 'Table Top Scales',
    description: 'Precision scales for commercial applications',
    image: '/assets/product-logo-boss-table-top-scale.png',
    imageFit: 'cover',
  },
  {
    id: 'software',
    name: 'Weighing Software',
    description: 'Weighbridge data management & integration software',
    image: '/assets/weighing-software.png',
    imageFit: 'cover',
  },
];

export const PRODUCTS = [
  // Weighbridges
  {
    id: 'pitless-weighbridge',
    slug: 'pitless-weighbridge',
    category: 'weighbridges',
    name: 'Pitless Weighbridge',
    tagline: 'Surface-mounted truck scale with easy installation',
    description: 'The Pitless Weighbridge is designed for surface-level installation without the need for civil foundation work. Ideal for locations where pit construction is not feasible or cost-effective. Features robust steel construction with modular design for easy transport and setup.',
    image: '/assets/product-logo-pitless-weighbridge.png',
    imageFit: 'cover',
    features: [
      'No pit construction required',
      'Quick and easy installation',
      'Modular steel platform design',
      'Approach ramps included',
      'Powder-coated finish for corrosion resistance',
      'High-accuracy load cells',
      'All-weather operation',
    ],
    applications: [
      'Mining & Quarries',
      'Logistics Hubs',
      'Manufacturing Plants',
      'Agricultural Facilities',
      'Construction Sites',
    ],
    specifications: {
      'Capacity': '10T to 200T (Customizable)',
      'Platform Size': '3m x 12m to 3.5m x 24m (Custom sizes available)',
      'Accuracy': '±20kg (Class III)',
      'Platform Material': 'MS IS2062 Grade Steel',
      'Load Cells': '4 to 8 (SESB-SBA Series)',
      'Operating Temp': '-10°C to +50°C',
      'Protection Class': 'IP68',
      'Customization': 'Fully customizable to customer & industrial needs',
    },
    scopeOfSupply: [
      'Weighbridge platform with approach ramps',
      'High-accuracy load cells (SESB-SBA Series)',
      'Digital weight indicator',
      'Junction box with surge protection',
      'Connecting cables',
      'Foundation drawings',
    ],
    optionalAccessories: [
      'Remote display',
      'Ticket printer',
      'PC-based software',
      'CCTV integration',
      'Boom barriers',
      'Traffic lights',
    ],
  },
  {
    id: 'pit-type-weighbridge',
    slug: 'pit-type-weighbridge',
    category: 'weighbridges',
    name: 'Pit Type Weighbridge',
    tagline: 'Flush-mounted weighbridge for seamless traffic flow',
    description: 'Pit Type Weighbridge is installed flush with the ground level, providing seamless vehicle movement without the need for approach ramps. Ideal for high-traffic areas where space optimization is crucial.',
    image: '/assets/product-logo-pit-type-weighbridge.png',
    imageFit: 'cover',
    features: [
      'Flush ground-level installation',
      'No approach ramps needed',
      'Space-efficient design',
      'Heavy-duty steel construction',
      'Excellent drainage system',
      'High-precision weighing',
      'Long service life',
    ],
    applications: [
      'Toll Plazas',
      'Industrial Plants',
      'Warehouses',
      'Distribution Centers',
      'Port Facilities',
    ],
    specifications: {
      'Capacity': '10T to 200T (Customizable)',
      'Platform Size': '3m x 9m to 3.5m x 24m (Custom sizes available)',
      'Accuracy': '±20kg (Class III)',
      'Platform Material': 'MS IS2062 Grade Steel',
      'Load Cells': '6 to 10 (SESB-SBA Series)',
      'Operating Temp': '-10°C to +50°C',
      'Protection Class': 'IP68',
      'Customization': 'Fully customizable to customer & industrial needs',
    },
    scopeOfSupply: [
      'Weighbridge platform',
      'Pit foundation drawings',
      'High-accuracy load cells',
      'Digital weight indicator',
      'Junction box',
      'Drainage system design',
    ],
    optionalAccessories: [
      'Jumbo remote display',
      'Thermal printer',
      'Weighbridge management software',
      'RFID/Smart card system',
      'ANPR camera system',
    ],
  },
  {
    id: 'weigh-in-motion',
    slug: 'weigh-in-motion',
    category: 'weighbridges',
    name: 'Weigh In Motion Scale',
    tagline: 'Avoid overloading, increase fuel economy, reduce wear & improve safety',
    description: 'Weigh In Motion (WIM) has been introduced to avoid overloading, increase fuel economy, reduce wear and improve safety on roads. WIM systems are designed to capture and record axle weights and gross vehicle weights as vehicles drive over the measurement site — precisely measuring and recording total vehicle weight while in motion. The LEION WIM features a fully welded steel structure with high-accuracy load cells that convert mechanical force into electrical impulses, delivering reliable bi-directional weighing with automatic unmanned recording capability.',
    image: '/assets/product-logo-weigh-in-motion.png',
    imageFit: 'cover',
    features: [
      'Captures & records axle weights and gross vehicle weights in motion',
      'Grouting frame holds module assembly at 0-0 road level for exemplary results',
      'Perfectly flat road of 18m before and 18m after the WIM for precision',
      'Road kerbs (0.6m height, 5m long) compel vehicles over weighing module',
      'Loop detectors with proper groove cutting and silicon covering for accuracy',
      'Load cells convert mechanical force into electrical impulses',
      'Fully welded steel structure for longevity',
      'Automatic unmanned recording with bi-directional weighing',
      'Dust and waterproof wirings throughout',
      'Data transmission via LAN or Internet',
    ],
    applications: [
      'Overload detection & prevention',
      'Fuel economy improvement',
      'Road wear reduction',
      'Traffic safety enforcement',
      'Traffic data collection',
      'Road maintenance planning',
      'Logistics hubs',
      'Mining operations',
    ],
    specifications: {
      'Capacity': '10T to 200T per axle (Customizable)',
      'Platform Size': '750mm x 3000mm / 700mm x 4000mm (Custom sizes available)',
      'Stationary Accuracy': '±0.05% FSR',
      'In-Motion Accuracy': '±1% at 5km/hr, ±2% at 10km/hr, ±3% at 15km/hr',
      'Load Cells': '4 x Alloy Steel (IP68) — transducers converting mechanical force to electrical impulses',
      'Material': 'MS IS 2062 with epoxy paint',
      'Grouting Frame': 'Installed at 0-0 road level, holds module assembly',
      'Road Preparation': '18m flat road before and after WIM',
      'Road Kerbs': '0.6m height, 5m long — channels vehicles over weighing module',
      'Direction': 'Bi-directional',
      'Operating Temp': '-10°C to 65°C',
      'Power Supply': 'AC 230V, 50Hz',
      'Controller Protection': 'IP65 rated',
      'Life Expectancy': '8 to 15 years',
      'Customization': 'Fully customizable to customer & industrial needs',
    },
    scopeOfSupply: [
      'Fully welded 750mm x 3000mm platform',
      'Grouting frame for module assembly',
      'Coping frame and mechanical accessories',
      '4 Alloy steel IP68 load cells',
      'Hermetically sealed junction box',
      'Indicator with RS232 connectivity',
      'Connection cables',
    ],
    optionalAccessories: [
      'Jumbo display',
      'Boom barriers for entry/exit',
      'Signal lights',
      'Rechargeable battery backup',
      'Loop detectors with silicon covering',
      'Loop accessories',
      'Road kerb installation kit',
    ],
  },
  // Indicators
  {
    id: 'hitech-combi-indicator',
    slug: 'hitech-combi-indicator',
    category: 'indicators',
    name: 'HITECH Combi Indicator (LE-07)',
    tagline: 'Alpha Numeric Weight Indicator with Advanced Features',
    description: 'The HITECH Combi (Model LE-07) is an advanced alpha-numeric weight indicator with USB connectivity, built-in SMS feature, and 2 Lac weighment record storage. Features 20-step calibration for higher accuracy and Excel format report generation via pen-drive.',
    image: '/assets/hitech-combi-indicator.jpg',
    imageFit: 'contain',
    features: [
      'USB Port for Keyboard - No outdated PS-2 keyboard needed',
      'USB Port for Pen-Drive - Dump data anytime without PC connection',
      'Excel Format Reports - View data directly on any computer',
      '2 Lac Weighment Records - No need to erase data frequently',
      'Built-in SMS Feature - Just attach modem to enable SMS',
      'SMS to Additional Mobile - Send SMS to customer or driver',
      '20 Step Calibration - Higher accuracy, rectifies load cell defects',
      '10 Ticket Entry with 25 char width - More information per weighment',
      '100 Codes for 3 entries - Speed up weighment process',
      'Time Report - Date & time of both weighments for analysis',
      'Field Programmable Preprinted Tickets - Customize in field',
      'LCD/LED Brightness Control - Saves electricity on battery',
    ],
    applications: [
      'Weighbridges',
      'Platform scales',
      'Industrial weighing',
      'Truck weighing',
      'Logistics & dispatch',
    ],
    specifications: {
      'Model': 'LE-07',
      'Display': 'LCD with backlight control',
      'Memory': '2,00,000 (2 Lac) weighment records',
      'USB Ports': '2 (Keyboard + Pen-drive)',
      'Data Export': 'Excel format via pen-drive',
      'Calibration': '20 Step with random load sequence',
      'Smart Calibration': '20 Step smart calibration',
      'Ticket Entries': '10 entries with 25 characters each',
      'Code Storage': '100 codes for 3 entries',
      'SMS Feature': 'Built-in (requires modem)',
      'Power Supply': 'AC 230V / Battery backup',
    },
    scopeOfSupply: [
      'HITECH Combi indicator unit',
      'Power adapter',
      'USB keyboard',
      'User manual',
      'Connecting cables',
    ],
    optionalAccessories: [
      'I-Net Application - Auto upload to web server, view from anywhere',
      'Wireless Display - No cabling needed, 100-200m range',
      'SMS Modem',
      'External printer',
      'Remote/Jumbo display',
    ],
  },
  {
    id: 'iw01-industrial-indicator',
    slug: 'iw01-industrial-indicator',
    category: 'indicators',
    name: 'IW01 Industrial Weight Indicator',
    tagline: 'Panel mount indicator for industrial automation',
    description: 'The IW01 Industrial Weight Indicator is a compact panel-mount solution designed for industrial automation and PLC integration. Features 8051 controller with 24-bit sigma-delta A/D converter for high precision weighing in harsh industrial environments.',
    image: '/assets/industrial-indicator.jpg',
    imageFit: 'contain',
    features: [
      '24-bit sigma-delta A/D converter',
      'Smart filter for vibration nullification',
      'Wide input voltage range (90V-270V AC)',
      'EMI and RFI suppression',
      'Panel mount design (96x48mm bezel)',
      'Easy software calibration',
      'Relay output for target achieved',
      'Withstands harsh industrial environment',
    ],
    applications: [
      'Industrial automation',
      'PLC integration',
      'Industrial control panels',
      'Concrete batching plants',
      'Process control',
    ],
    specifications: {
      'Processor': '8051 Controller @ 18MHz',
      'A/D Resolution': '24-bit Sigma Delta',
      'Conversion Speed': '20 samples/second',
      'Display': '5 digit 1½" RED LED',
      'Input Voltage': '90V to 270V AC @ 50Hz',
      'Load Cell Support': 'Up to 6 load cells (350 ohms)',
      'Operating Temp': '0°C to 55°C',
      'Case Size': '92x44x120mm',
      'Protection': 'Spike suppressor, EMI/RFI filter',
    },
    scopeOfSupply: [
      'IW01 indicator unit',
      'Mounting hardware',
      'User manual',
      'Connection cable',
    ],
    optionalAccessories: [
      'RS232 communication module',
      'RS485 Modbus module',
      '4-20mA analog output',
      '0-10V analog output',
      'External relay module',
    ],
  },
  {
    id: 'le-k9-indicator',
    slug: 'le-k9-indicator',
    category: 'indicators',
    name: 'LE-K9 Platform Scale Indicator',
    tagline: 'Compact platform scale with LED display',
    description: 'The LE-K9 (Model LEP-300) is a versatile platform weighing scale with 300kg capacity, perfect for industrial and commercial applications. Features a durable 500x500mm platform with rechargeable Li-ion battery for portable operation and bright RED LED display for easy reading.',
    image: '/assets/product-logo-platform-scale.png',
    imageFit: 'cover',
    features: [
      '300kg maximum capacity',
      '50g readability for precise weighing',
      'Large 500x500mm platform',
      'Rechargeable Li-ion battery (3.7V)',
      '0.6" Seven Segment RED LED display',
      'Wide operating temperature (0-45°C)',
      '5V DC load cell input voltage',
      'AC 230V 50-60Hz power supply',
    ],
    applications: [
      'Warehouse weighing',
      'Factory floor weighing',
      'Shipping & receiving',
      'Agricultural produce',
      'General industrial use',
      'Commercial trading',
    ],
    specifications: {
      'Model': 'LEP-300',
      'Max Capacity': '300 kg',
      'Readability': '50 g',
      'Platform Size': '500 x 500 mm',
      'Operating Temp': '0°C to 45°C',
      'Load Cell Voltage': '5V DC',
      'Battery': 'Li-ion 3.7V Rechargeable',
      'Display': '0.6" Seven Segment RED LED',
      'Power Supply': '230V 50-60Hz',
    },
    scopeOfSupply: [
      'LE-K9 platform scale unit',
      'Display indicator',
      'Power adapter',
      'Rechargeable battery',
      'User manual',
    ],
    optionalAccessories: [
      'External display',
      'Printer interface',
      'RS232 connectivity',
      'Larger platform options',
      'Ramp for wheel-on access',
    ],
  },
  // Table Top Scales
  {
    id: 'boss-series-table-top',
    slug: 'boss-series-table-top',
    category: 'table-top-scales',
    name: 'BOSS Series Table Top Scale',
    tagline: 'Ultimate performance and price package',
    description: 'The BOSS Series offers ultimate performance and value. Features up to 100,000 external display divisions with piece counting, percentage weighing, and auto accumulation modes. Large 0.8" LED digits for easy viewing from front and pole display.',
    image: '/assets/product-logo-boss-table-top-scale.png',
    imageFit: 'cover',
    features: [
      'Up to 100,000 display divisions',
      'Piece counting mode',
      'Percentage weighing',
      'Auto accumulation',
      'Large 0.8" LED digits',
      'Auto zero, glow control',
      'Pre-tare, anti-vibration, animal mode',
      '40 hrs rechargeable battery backup',
      'Thermal/dot matrix printer support',
      'Weight verification with 10x zoom',
    ],
    applications: [
      'Grocery stores',
      'Paint mixing',
      'Parcel weighing',
      'Fruits & vegetables',
      'Meats & cheeses',
      'Packaging',
      'Check weighing',
    ],
    specifications: {
      'Capacity Range': '3kg to 30kg',
      'Division Range': '0.1g to 5g',
      'Display': '0.8" LED (Red/Green)',
      'Power': 'AC 110V/220V/240V or DC 6V/4AH',
      'Dimensions': '429x350x409mm',
      'Box Size': '483x410x256mm',
      'Weight': '6.7kg gross',
    },
    scopeOfSupply: [
      'Table top scale unit',
      'Weighing pan',
      'Power adapter',
      'Rechargeable battery',
      'User manual',
    ],
    optionalAccessories: [
      'Pole display',
      'Rear display',
      'Large platter',
      'RS232 output',
      'Direct printer interface',
      'Date/time module',
    ],
  },
  // Software
  {
    id: 'weighbridge-integration-software',
    slug: 'weighbridge-integration-software',
    category: 'software',
    name: 'Weighbridge Integration Software',
    tagline: 'Complete weighbridge data management solution',
    description: 'Weighment software is a weighbridge data management software suitable for industries as well as public weighbridges. The standalone weighment software is suitable when all weighment data is to be stored on PC connected to the weighing indicator. The software is very popular where the real image of the vehicle at the time of weighment is captured and is stored in the PC and can be viewed and printed at anytime. We customize weighbridge software as per customer requirement so it suits their needs.',
    image: '/assets/weighing-software.png',
    imageFit: 'cover',
    features: [
      'Custom Solution — Customized solution according to client requirements',
      'Easy Weighment Process — Simple and easy to operate',
      'Ticket/Report Printing — Vehicle wise, Customer wise, Material wise reports',
      'WhatsApp Messaging — Send each slip to your WhatsApp mobile number',
      'Field Configurable Settings — All settings are field configurable',
      'SAP & ERP Integration — Integration according to client requirements',
      'Email & SMS Alerts — Automated alerts on each weighment',
      'Printer Support — Dotmatrix & Laser printer compatibility',
      'Photo Capturing — Supports IP Cameras with RTSP support',
      'Traffic Light Control — Integrate & control traffic lights',
      'RFID Card Integration — Automatic save transaction with RFID cards',
      'Boom Barrier Integration — Automated boom barrier control',
    ],
    applications: [
      'Industrial weighbridges',
      'Public weighbridges',
      'Mining & quarry operations',
      'Logistics & transport hubs',
      'Manufacturing plants',
      'Agricultural facilities',
      'Construction sites',
    ],
    specifications: {
      'Software Type': 'Standalone / Networked',
      'Data Storage': 'PC-based with database',
      'Camera Support': 'IP Cameras with RTSP protocol',
      'Printer Support': 'Dotmatrix & Laser printers',
      'Integration': 'SAP & ERP compatible',
      'Communication': 'WhatsApp, Email & SMS alerts',
      'Access Control': 'RFID Card integration',
      'Automation': 'Traffic lights & Boom barriers',
      'Reports': 'Vehicle wise, Customer wise, Material wise',
      'Customization': 'Fully customizable to customer requirements',
    },
    scopeOfSupply: [
      'Weighment software license',
      'Installation & configuration',
      'User training',
      'Technical documentation',
      'Customization as per requirement',
    ],
    optionalAccessories: [
      'IP Camera for photo capturing',
      'RFID Card reader',
      'Boom barrier integration kit',
      'Traffic light control module',
      'SMS modem',
      'Additional PC/workstation licenses',
    ],
  },
];

export const getProductsByCategory = (categoryId) => {
  return PRODUCTS.filter(product => product.category === categoryId);
};

export const getProductBySlug = (slug) => {
  return PRODUCTS.find(product => product.slug === slug);
};

export const getCategoryById = (categoryId) => {
  return PRODUCT_CATEGORIES.find(cat => cat.id === categoryId);
};
